import { DpRuleSetSchema } from "./dp-rules.schemas.js";
import { z } from "zod";
//#region src/observability.schemas.ts
/**
* Zod schemas for the CP → DP observability configuration block.
* The release-engine populates these inside the DesiredStateEnvelope; the
* DP operator reads + reconciles.
*
* The DP telemetry residency members (`metrics.retentionDays`, `traces.retentionDays`,
* `cpFullExport`, `dpRules`) are optional, so a CP that does not write them still serves a DP.
* `resolveDpStoreSettings` in `observability.ts` is the one rule that gives an absent member its
* meaning. Every other member is required.
*
* No schema here uses `.transform()` or `z.custom`: `ObservabilityConfigSchema` sits inside the
* release-engine desired-state DTO, and `createOpenApiDocument()` throws on either at boot.
*/
/**
* The telemetry route mode of one DP. Exactly these three values, as spelled; any other value,
* a case variant included, fails.
*
* - `push_to_cp`: the DP collector sends full metrics and traces to the CP gateway.
* - `cloud_native`: the DP sends telemetry to the cloud provider's own services.
* - `local`: the DP collector writes full metrics to the DP Mimir and full traces to the DP Tempo.
*   Logs stay in the DP Loki. The DP sends the fleet-only stream to the CP.
*
* `ObservabilityStatusSchema` uses this enum, so `StatusReportSchema` accepts `local`. A CP older
* than this enum refuses a `local` status report; the rollout order (contracts, then the CP, then
* the operator program) is the guard.
*/
const TelemetryModeSchema = z.enum([
	"push_to_cp",
	"cloud_native",
	"local"
]);
/**
* The days a DP store (the DP Mimir or the DP Tempo) keeps data: an integer from 1 to 365.
* A fraction, 0, a negative number and 366 fail. 0 fails because Mimir reads a zero
* `compactor_blocks_retention_period` as "keep forever".
*/
const DpRetentionDaysSchema = z.number().int().min(1).max(365);
const LogsConfigSchema = z.object({
	enabled: z.boolean(),
	retentionDays: z.number().int().nonnegative(),
	ingestionRateLimitMbps: z.number().nonnegative()
}).strict();
/**
* Metrics configuration. `retentionDays` (optional, absent means 30) is the retention of the DP
* Mimir. Outside mode `local` the DP has no DP Mimir, and the operator program ignores it.
*/
const MetricsConfigSchema = z.object({
	enabled: z.boolean(),
	pushIntervalSeconds: z.number().int().positive(),
	retentionDays: DpRetentionDaysSchema.optional()
}).strict();
/**
* Traces configuration. `retentionDays` (optional, absent means 14) is the retention of the DP
* Tempo. Outside mode `local` the DP has no DP Tempo, and the operator program ignores it.
*/
const TracesConfigSchema = z.object({
	enabled: z.boolean(),
	samplingRate: z.number().min(0).max(1),
	retentionDays: DpRetentionDaysSchema.optional()
}).strict();
const AlertingConfigSchema = z.object({
	enabled: z.boolean(),
	errorRateThresholdPercent: z.number().min(0).max(100),
	p99LatencyThresholdSeconds: z.number().nonnegative()
}).strict();
/**
* The CP-to-DP observability configuration, `DesiredStateEnvelope.observability`.
*
* Two optional members serve mode `local`:
* - `cpFullExport`: `true` means the DP also sends the full metric and trace streams to the CP
*   (the 30-day dual path); `false` means the fleet-only stream alone. Absent means `true`.
* - `dpRules`: the `DpRuleSet` for the DP Mimir ruler. Absent means the CP manages no rule for
*   this DP.
*
* Every object is `.strict()`: an unknown key at any level fails.
*/
const ObservabilityConfigSchema = z.object({
	telemetryMode: TelemetryModeSchema,
	logs: LogsConfigSchema,
	metrics: MetricsConfigSchema,
	traces: TracesConfigSchema,
	alerting: AlertingConfigSchema,
	cpFullExport: z.boolean().optional(),
	dpRules: DpRuleSetSchema.optional()
}).strict();
/** The observability section of the DP status report. `telemetryMode` takes all three modes. */
const ObservabilityStatusSchema = z.object({
	retentionDays: z.number().int().nonnegative(),
	samplingRate: z.number().min(0).max(1),
	pushIntervalSeconds: z.number().int().positive(),
	telemetryMode: TelemetryModeSchema,
	lastConfigChangeAt: z.string().min(1),
	errors: z.array(z.string())
}).strict();
//#endregion
export { AlertingConfigSchema, DpRetentionDaysSchema, LogsConfigSchema, MetricsConfigSchema, ObservabilityConfigSchema, ObservabilityStatusSchema, TelemetryModeSchema, TracesConfigSchema };

//# sourceMappingURL=observability.schemas.js.map